import React from "react";
import { Box, DrawerBody, DrawerHeader, Flex, IconButton, Image, Tag, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { colours, qoriaColours } from "@/theme/colours";
import { useSignatureStore } from "@/stores/v2/signatureStore";
import { MAX_RULES_PER_LIST } from "./constants";
import { List } from "./types";

interface Props {
	list: List;
	onBack: () => void;
	onDeleteRule: (ruleId: string) => void;
	isDeleting?: boolean;
}

const sectionLabelProps = {
	fontSize: "sm",
	fontWeight: 700,
	color: colours.text.regular,
} as const;

export function QuickBlockListView(props: Props): JSX.Element {
	const { t } = useTranslation();
	const { list, onBack, onDeleteRule, isDeleting = false } = props;
	const lookupName = useSignatureStore((state) => state.lookupName);
	const getFavicon = useSignatureStore((state) => state.getFavicon);
	const findSignature = useSignatureStore((state) => state.findSignature);

	return (
		<>
			<DrawerHeader borderBottom="1px solid" borderColor={qoriaColours.brand.neutral[100]} px={6} py={4}>
				<Flex align="center" gap={3}>
					<IconButton
						aria-label={t("rulesLibrary.form.back")}
						variant="ghost"
						onClick={onBack}
						icon={<ChakraFAIcon icon="fa-arrow-left" variant="solid" color={colours.text.regular} boxSize={4} />}
					/>
					<Text fontSize="xl" fontWeight="medium" color={colours.text.title}>
						{t("rulesLibrary.form.editListTitle")}
					</Text>
				</Flex>
			</DrawerHeader>

			<DrawerBody px={6} py={5}>
				<Text fontSize="2xl" fontWeight={500} color={colours.text.title} mb={6}>
					{t("rulesLibrary.quickBlock.listTitle")}
				</Text>

				<Text fontSize="xl" fontWeight="medium" color={colours.text.title} mb={3}>
					{t("rulesLibrary.form.rulesHeading")}
				</Text>
				<Text fontSize="sm" color={colours.text.regular} mb={4}>
					{t("rulesLibrary.quickBlock.listHelper")}
				</Text>

				<Box border="1px solid" borderColor={qoriaColours.brand.neutral[100]} rounded="xl" overflow="hidden">
					<Flex
						justify="space-between"
						align="center"
						bg={qoriaColours.brand.neutral[50]}
						borderBottom="1px solid"
						borderColor={qoriaColours.brand.neutral[100]}
						px={4}
						py={3}
					>
						<Text {...sectionLabelProps}>{t("rulesLibrary.form.rulesInList")}</Text>
						<Text fontSize="sm" color={colours.text.regular}>
							{t("rulesLibrary.form.rulesCounter", { count: list.rules.length, max: MAX_RULES_PER_LIST })}
						</Text>
					</Flex>

					{list.rules.map((rule, index) => {
						const name = lookupName(rule.target);
						const favicon = findSignature(rule.target) ? getFavicon(rule.target) : undefined;

						return (
							<Flex
								key={rule.id}
								align="center"
								gap={3}
								px={4}
								py={3}
								borderTop={index === 0 ? undefined : "1px solid"}
								borderColor={qoriaColours.brand.neutral[50]}
							>
								<Flex align="center" justify="center" boxSize={10} rounded="lg" bg={qoriaColours.brand.neutral[50]} flexShrink={0}>
									{favicon ? (
										<Image src={favicon} alt="" boxSize={6} />
									) : (
										<ChakraFAIcon icon="fa-shield-halved" variant="solid" color={colours.text.regular} boxSize={6} />
									)}
								</Flex>
								<Box flex={1}>
									<Text fontWeight={500} color={colours.text.title}>
										{name}
									</Text>
									<Text fontSize="sm" color={colours.text.regular}>
										{t("rulesLibrary.quickBlock.blockDescription", { label: name.toLowerCase() })}
									</Text>
								</Box>
								<Tag bg={qoriaColours.brand.danger[10]} color={qoriaColours.brand.danger[500]} fontWeight={500} gap={2}>
									<ChakraFAIcon icon="fa-ban" variant="solid" boxSize={3} />
									{t("rulesLibrary.ruleStatusBlocked")}
								</Tag>
								<IconButton
									aria-label={t("rulesLibrary.form.deleteRuleAria", { target: name })}
									variant="ghost"
									isDisabled={isDeleting}
									onClick={() => onDeleteRule(rule.id)}
									icon={<ChakraFAIcon icon="fa-trash-can" variant="regular" color={qoriaColours.brand.danger[500]} boxSize={4} />}
								/>
							</Flex>
						);
					})}
				</Box>
			</DrawerBody>
		</>
	);
}
